document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const TFS = window.TFS || {};
const root = TFS.root || '/';
const qs = (selector, scope = document) => scope.querySelector(selector);
const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
const status = (message) => {
  const region = qs('[data-a11y-status]');
  if (region) region.textContent = message;
};
const money = (cents) => new Intl.NumberFormat(TFS.locale || document.documentElement.lang, {
  style: 'currency',
  currency: TFS.currency || 'EUR'
}).format(Number(cents || 0) / 100);

const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
let activeOverlay = null;
let returnFocus = null;

function trapFocus(event) {
  if (!activeOverlay || event.key !== 'Tab') return;
  const focusable = qsa(focusableSelector, activeOverlay).filter((el) => !el.hidden && el.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function openOverlay(element, trigger, bodyClass) {
  if (!element) return;
  if (activeOverlay && activeOverlay !== element) closeOverlay(activeOverlay);
  returnFocus = trigger || document.activeElement;
  element.hidden = false;
  element.setAttribute('aria-hidden', 'false');
  trigger?.setAttribute('aria-expanded', 'true');
  if (bodyClass) element.dataset.bodyClass = bodyClass;
  if (bodyClass) document.body.classList.add(bodyClass);
  activeOverlay = element;
  requestAnimationFrame(() => qs(focusableSelector, element)?.focus());
}

function closeOverlay(element = activeOverlay) {
  if (!element) return;
  element.setAttribute('aria-hidden', 'true');
  const controls = element.id ? qs(`[aria-controls="${CSS.escape(element.id)}"]`) : null;
  controls?.setAttribute('aria-expanded', 'false');
  const bodyClass = element.dataset.bodyClass;
  if (bodyClass) document.body.classList.remove(bodyClass);
  window.setTimeout(() => { if (element.getAttribute('aria-hidden') === 'true') element.hidden = true; }, 360);
  if (activeOverlay === element) activeOverlay = null;
  returnFocus?.focus?.();
  returnFocus = null;
}

function initOverlays() {
  const menu = qs('[data-mobile-menu]');
  const menuOpen = qs('[data-menu-open]');
  menuOpen?.addEventListener('click', () => openOverlay(menu, menuOpen, 'menu-open'));
  qs('[data-menu-close]')?.addEventListener('click', () => closeOverlay(menu));

  qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => {
    try { await refreshCart(true, button); } catch (error) { /* The drawer displays the recoverable error. */ }
  }));
  qs('[data-cart-close]')?.addEventListener('click', () => closeCart());
  qs('[data-cart-backdrop]')?.addEventListener('click', () => closeCart());

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeOverlay) closeOverlay(activeOverlay);
    trapFocus(event);
  });
}

function initHeader() {
  const header = qs('[data-header]');
  if (!header) return;
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 20);
  onScroll();
  addEventListener('scroll', onScroll, { passive: true });
}

function initCountdown() {
  qsa('[data-drop-date]').forEach((bar) => {
    const output = qs('[data-countdown]', bar);
    const target = Date.parse(bar.dataset.dropDate || '');
    if (!output || Number.isNaN(target)) return;
    let interval;
    const tick = () => {
      const distance = target - Date.now();
      if (distance <= 0) {
        output.textContent = bar.dataset.liveText || '';
        if (bar.dataset.hideExpired === 'true') bar.hidden = true;
        if (interval) clearInterval(interval);
        return;
      }
      const days = Math.floor(distance / 86400000);
      const hours = Math.floor((distance % 86400000) / 3600000);
      const minutes = Math.floor((distance % 3600000) / 60000);
      output.textContent = `J-${days} · ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };
    tick();
    interval = window.setInterval(tick, 60000);
  });
}

function parseProduct(rootElement) {
  try {
    return JSON.parse(qs('[data-product-json]', rootElement)?.textContent || '{}');
  } catch (error) {
    return { variants: [] };
  }
}

function initProduct() {
  qsa('[data-product-root]').forEach((productRoot) => {
    const productData = parseProduct(productRoot);
    const variants = productData.variants || [];
    const form = qs('[data-product-form]', productRoot);
    const idInput = qs('[data-variant-id]', productRoot);
    const price = qs('[data-product-price]', productRoot);
    const compare = qs('[data-product-compare]', productRoot);
    const sku = qs('[data-product-sku]', productRoot);
    const addButton = qs('[data-add-to-cart]', productRoot);
    const errorBox = qs('[data-product-error]', productRoot);
    const stockLabel = qs('[data-stock-label]', productRoot);
    const stockDot = qs('.status-dot', productRoot);
    const stickyBar = qs('[data-product-sticky]');
    const stickyButton = qs('[data-sticky-submit]', stickyBar || document);
    const stickyPrice = qs('[data-sticky-price]', stickyBar || document);
    const preorderLabel = productRoot.dataset.preorderLabel || '';

    const selections = () => qsa('[data-option-index]', productRoot).map((group) => qs('input:checked', group)?.value);

    const updateOptionAvailability = () => {
      const selected = selections();
      qsa('[data-option-index]', productRoot).forEach((group, optionIndex) => {
        qsa('input', group).forEach((input) => {
          const possible = variants.some((variant) => {
            if (!variant.available || variant.options[optionIndex] !== input.value) return false;
            return variant.options.slice(0, optionIndex).every((value, index) => value === selected[index] || selected[index] === undefined);
          });
          input.disabled = !possible;
          input.parentElement?.classList.toggle('is-unavailable', !possible);
        });
        const checked = qs('input:checked', group);
        if (checked?.disabled) qs('input:not(:disabled)', group)?.click();
        const selectedLabel = qs('[data-selected-option]', group);
        if (selectedLabel) selectedLabel.textContent = qs('input:checked', group)?.value || '';
      });
    };

    const setButtons = (disabled, label) => {
      if (addButton) { addButton.disabled = disabled; addButton.textContent = label; }
      if (stickyButton) { stickyButton.disabled = disabled; stickyButton.textContent = label; }
    };

    const updateVariant = (shouldScroll = false) => {
      updateOptionAvailability();
      const optionGroups = qsa('[data-option-index]', productRoot);
      const selected = selections();
      const complete = optionGroups.length === 0 || selected.every(Boolean);

      if (!complete) {
        if (idInput) idInput.value = '';
        productRoot.dataset.needsSize = '1';
        setButtons(false, TFS.strings?.chooseSize || 'Choisir une taille');
        return;
      }
      productRoot.dataset.needsSize = '';

      const variant = variants.find((candidate) => candidate.options.every((value, index) => selected[index] === undefined || value === selected[index]));
      if (!variant) {
        if (idInput) idInput.value = '';
        setButtons(true, TFS.strings?.unavailable || 'Unavailable');
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (price) price.textContent = money(variant.price);
      if (compare) {
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : '';
      }
      if (sku) {
        sku.hidden = !variant.sku;
        const value = qs('span', sku);
        if (value) value.textContent = variant.sku || '';
      }
      const addLabel = variant.available
        ? (preorderLabel || TFS.strings?.add || 'Add to cart')
        : (TFS.strings?.soldOut || 'Sold out');
      setButtons(!variant.available, addLabel);
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      if (stockLabel) {
        if (!variant.available) stockLabel.textContent = TFS.strings?.soldOut || 'Sold out';
        else if (!preorderLabel) stockLabel.textContent = TFS.strings?.inStock || 'In stock';
      }
      stockDot?.classList.toggle('status-dot--off', !variant.available);
      if (shouldScroll && variant.featured_media_id) qs(`[data-media-id="${variant.featured_media_id}"]`, productRoot)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    };

    qsa('[data-option-index] input', productRoot).forEach((input) => input.addEventListener('change', () => updateVariant(true)));
    updateVariant(false);

    stickyButton?.addEventListener('click', () => form?.requestSubmit());
    if (stickyBar && addButton && 'IntersectionObserver' in window) {
      const stickyObserver = new IntersectionObserver(([entry]) => stickyBar.classList.toggle('is-visible', !entry.isIntersecting), { threshold: .12 });
      stickyObserver.observe(addButton);
    }

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (productRoot.dataset.needsSize) {
        if (errorBox) {
          errorBox.textContent = TFS.strings?.chooseSizeError || 'Choisis ta taille pour continuer';
          errorBox.hidden = false;
        }
        status(TFS.strings?.chooseSizeError || 'Choisis ta taille pour continuer');
        const firstOption = qs('[data-option-index]', productRoot);
        firstOption?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        qs('input:not(:disabled)', firstOption || productRoot)?.focus({ preventScroll: true });
        return;
      }
      if (!addButton || addButton.disabled || !idInput?.value) return;
      if (errorBox) errorBox.hidden = true;
      addButton.disabled = true;
      addButton.textContent = TFS.strings?.adding || 'Adding…';
      try {
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || TFS.strings?.cartError);
        addButton.textContent = TFS.strings?.added || 'Added';
        status(addButton.textContent);
        await refreshCart(true, addButton);
        window.setTimeout(() => updateVariant(false), 900);
      } catch (error) {
        errorBox.textContent = error.message || TFS.strings?.cartError;
        errorBox.hidden = false;
        addButton.disabled = false;
        addButton.textContent = preorderLabel || TFS.strings?.add || 'Add to cart';
      }
    });
  });
}

function createElement(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function createCartLine(item) {
  const article = createElement('article', 'cart-line');
  const media = createElement('a', 'cart-line__media');
  media.href = item.url;
  if (item.image) {
    const image = new Image();
    image.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=240`;
    image.alt = item.product_title;
    image.loading = 'lazy';
    media.append(image);
  }

  const content = createElement('div', 'cart-line__content');
  const title = createElement('a', 'cart-line__title', item.product_title);
  title.href = item.url;
  content.append(title);
  if (item.variant_title) content.append(createElement('small', '', item.variant_title));
  if (item.properties) Object.entries(item.properties).forEach(([key, value]) => {
    if (value && !key.startsWith('_')) content.append(createElement('small', '', `${key}: ${value}`));
  });
  content.append(createElement('strong', 'cart-line__price', money(item.final_line_price)));

  const quantity = createElement('div', 'quantity-control');
  const decrease = createElement('button', '', '−');
  decrease.type = 'button';
  decrease.setAttribute('aria-label', TFS.strings?.decrease || 'Decrease quantity');
  const amount = createElement('span', '', String(item.quantity));
  amount.setAttribute('aria-live', 'polite');
  const increase = createElement('button', '', '+');
  increase.type = 'button';
  increase.setAttribute('aria-label', TFS.strings?.increase || 'Increase quantity');
  decrease.addEventListener('click', () => changeCartLine(item.key, Math.max(0, item.quantity - 1)));
  increase.addEventListener('click', () => changeCartLine(item.key, item.quantity + 1));
  quantity.append(decrease, amount, increase);

  const remove = createElement('button', 'cart-line__remove', TFS.strings?.remove || 'Remove');
  remove.type = 'button';
  remove.addEventListener('click', () => changeCartLine(item.key, 0));
  content.append(quantity, remove);
  article.append(media, content);
  return article;
}

function updateCartProgress(cart) {
  const threshold = Number(TFS.freeShippingThreshold || 0);
  const message = qs('[data-shipping-message]');
  const progress = qs('[data-shipping-progress]');
  if (!message || !progress) return;
  const remaining = Math.max(0, threshold - cart.total_price);
  const percent = threshold > 0 ? Math.min(100, Math.round((cart.total_price / threshold) * 100)) : 100;
  const template = remaining > 0 ? TFS.strings?.freeShippingRemaining : TFS.strings?.freeShippingUnlocked;
  message.textContent = remaining > 0 ? String(template || '').replace('{{ amount }}', money(remaining)) : (template || 'Free shipping unlocked');
  progress.style.setProperty('--progress', `${percent}%`);
  progress.setAttribute('aria-valuenow', String(percent));
}

async function getCart() {
  const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(TFS.strings?.cartError || 'Cart error');
  return response.json();
}

async function refreshCart(open = false, trigger) {
  const lines = qs('[data-cart-lines]');
  const errorBox = qs('[data-cart-error]');
  if (lines) lines.setAttribute('aria-busy', 'true');
  if (errorBox) errorBox.hidden = true;
  try {
    const cart = await getCart();
    qsa('[data-cart-count]').forEach((element) => { element.textContent = cart.item_count; });
    const total = qs('[data-cart-total]');
    if (total) total.textContent = money(cart.total_price);
    updateCartProgress(cart);
    if (lines) {
      lines.replaceChildren();
      if (cart.items.length) cart.items.forEach((item) => lines.append(createCartLine(item)));
      else lines.append(createElement('p', 'cart-drawer__empty', TFS.strings?.emptyCart || 'Your cart is empty.'));
    }
    if (open) openCart(trigger);
    return cart;
  } catch (error) {
    if (errorBox) {
      errorBox.textContent = error.message;
      errorBox.hidden = false;
    }
    throw error;
  } finally {
    if (lines) lines.setAttribute('aria-busy', 'false');
  }
}

async function changeCartLine(key, quantity) {
  const lines = qs('[data-cart-lines]');
  lines?.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || TFS.strings?.cartError);
    await refreshCart();
    status(TFS.strings?.updated || 'Cart updated');
  } catch (error) {
    const box = qs('[data-cart-error]');
    if (box) { box.textContent = error.message; box.hidden = false; }
  }
}

function openCart(trigger) {
  const drawer = qs('[data-cart-drawer]');
  const backdrop = qs('[data-cart-backdrop]');
  if (backdrop) backdrop.hidden = false;
  openOverlay(drawer, trigger, 'drawer-open');
}

function closeCart() {
  const drawer = qs('[data-cart-drawer]');
  closeOverlay(drawer);
  const backdrop = qs('[data-cart-backdrop]');
  window.setTimeout(() => { if (backdrop) backdrop.hidden = true; }, 360);
}

function initQuickAdd() {
  qsa('[data-quick-add]').forEach((form) => {
    if (form.dataset.quickAddBound === 'true') return;
    form.dataset.quickAddBound = 'true';
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('button', form);
      const errorBox = qs('[data-quick-add-error]', form);
      if (errorBox) errorBox.hidden = true;
      button.disabled = true;
      try {
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || TFS.strings?.cartError);
        await refreshCart(true, button);
      } catch (error) {
        if (errorBox) { errorBox.textContent = error.message; errorBox.hidden = false; }
      } finally {
        button.disabled = false;
      }
    });
  });
}

function initRecommendations() {
  qsa('[data-product-recommendations]').forEach(async (container) => {
    if (!container.dataset.url || container.innerHTML.trim()) return;
    try {
      const response = await fetch(container.dataset.url);
      if (!response.ok) return;
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const updated = qs('[data-product-recommendations]', template.content);
      if (updated?.innerHTML.trim()) {
        container.replaceWith(updated);
        initQuickAdd();
        // Injected cards carry [data-reveal], which editorial-v3.css holds at
        // opacity 0 until initReveal()'s observer adds .is-visible. Without
        // this call the recommendations stay invisible forever. Re-running it
        // is idempotent: classList.add and unobserve are both no-ops on nodes
        // the first observer already handled.
        initReveal();
      }
    } catch (error) {
      // Recommendations are non-critical and fail silently.
    }
  });
}

function initForms() {
  qsa('[data-auto-submit], [data-localization-select]').forEach((select) => select.addEventListener('change', () => select.form?.submit()));
}

function initGridDensity() {
  const grid = qs('[data-product-grid]');
  const buttons = qsa('[data-grid-density]');
  if (!grid || !buttons.length) return;
  let selected = 'editorial';
  try { selected = localStorage.getItem('tfs-grid-density') || selected; } catch (error) { /* Storage is optional. */ }
  const apply = (density) => {
    selected = density === 'compact' ? 'compact' : 'editorial';
    grid.dataset.density = selected;
    buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.gridDensity === selected)));
    try { localStorage.setItem('tfs-grid-density', selected); } catch (error) { /* Storage is optional. */ }
  };
  buttons.forEach((button) => button.addEventListener('click', () => apply(button.dataset.gridDensity)));
  apply(selected);
}

function initReveal() {
  const elements = qsa('[data-reveal]');
  if (!elements.length) return;
  if (!('IntersectionObserver' in window)) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -7% 0px', threshold: .08 });
  elements.forEach((element) => observer.observe(element));
}

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initCountdown();
  initOverlays();
  initProduct();
  initQuickAdd();
  initRecommendations();
  initForms();
  initGridDensity();
  initReveal();
});
